import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CreditCard, Clock, Lock, Check, Info } from "lucide-react";

// Standalone trial-subscribe page for admin-invited users (vendor / event
// creator / marketplace seller). This page is the invite flow's replacement
// for /subscribe — the existing Subscribe page is NOT modified. Approved
// invited users land here via the dashboardFor routing override.
//
// What happens on this page:
// 1. GET /invite-trial/status fetches the role + launch plan + trial state.
// 2. If `trial_captured` is already true, show a success state and route to
//    the role portal after a short beat.
// 3. Otherwise, render a one-click "Add my card" button that POSTs
//    /invite-trial/initialize and redirects the user to Paystack. The
//    callback URL comes back to /invite-trial-callback, which verifies the
//    R1 capture, schedules the subscription for +90 days, refunds the R1,
//    and routes the user to their portal.
export default function InviteTrialSubscribe() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/invite-trial/status");
        if (cancelled) return;
        setStatus(data);
        if (data.trial_captured) {
          // Trial already set up — refresh user and bounce to portal.
          const fresh = await refresh();
          toast.success("Your trial is already active");
          setTimeout(() => navigate(fresh ? dashboardFor(fresh) : "/"), 1200);
        }
      } catch (e) {
        if (cancelled) return;
        toast.error(errMsg(e, "Could not load your invite trial"));
        navigate("/");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, refresh]);

  const startCapture = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/invite-trial/initialize");
      if (!data?.authorization_url) throw new Error("No authorization URL returned");
      // Full page redirect — session continues via secure cookies.
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start the trial"));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-loading">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 text-center text-forest/60">
          Loading your trial…
        </div>
      </div>
    );
  }

  if (status?.trial_captured) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-already-captured">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 max-w-md w-full text-center shadow-sm fade-in">
          <div className="w-14 h-14 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-sage-dark" />
          </div>
          <h1 className="text-xl font-black text-forest mb-2">Trial already active</h1>
          <p className="text-sm text-forest/70">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  const rand = status?.plan_amount_cents != null ? (status.plan_amount_cents / 100).toFixed(0) : "149";
  const trialEnd = new Date(Date.now() + ((status?.trial_days || 90) * 86400000));
  const trialEndStr = trialEnd.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-lg w-full shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-10 h-10 rounded-full bg-terracotta flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </span>
          <div>
            <h1 className="text-xl font-black text-forest leading-tight">Start your 90-day free trial</h1>
            <p className="text-xs text-forest/60" data-testid="invite-trial-role">{status?.role_label} membership</p>
          </div>
        </div>

        <div className="rounded-2xl bg-sage-light/60 border-2 border-sage/40 p-5 mb-5 space-y-3" data-testid="invite-trial-summary">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">Free for 90 days</p>
              <p className="text-xs text-forest/70">No charge today. Your first {`R${rand}`} payment is scheduled for <strong data-testid="invite-trial-end-date">{trialEndStr}</strong>.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">Why we ask for your card</p>
              <p className="text-xs text-forest/70">We charge a tiny <strong>R1 verification</strong> and refund it right away. This saves your card so the first real payment can happen automatically when your trial ends.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-forest text-sm">Cancel anytime</p>
              <p className="text-xs text-forest/70">Cancel from your profile before {trialEndStr} and you'll never be billed.</p>
            </div>
          </div>
        </div>

        <p
          className="text-[11px] text-forest/60 mb-3 flex items-start gap-1.5 leading-snug"
          data-testid="r1-verification-notice"
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Paystack charges a refundable R1 card verification fee to activate your payment method. It's refunded in full and is not part of your subscription cost.</span>
        </p>
        <Button
          onClick={startCapture}
          disabled={submitting || !status?.gateway_ready}
          className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md"
          data-testid="invite-trial-start-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {submitting ? "Opening secure checkout…" : `Add my card and start the 90-day trial`}
        </Button>
        {!status?.gateway_ready && (
          <p className="mt-3 text-xs text-center text-terracotta-dark" data-testid="invite-trial-gateway-off">
            Payment gateway is not available right now. Please try again later.
          </p>
        )}

        <p className="text-center text-xs text-forest/50 mt-6">
          Signed in as <strong>{user?.email}</strong> · {status?.role_label} invited via admin link
        </p>
        <p className="text-center text-xs text-forest/40 mt-2">
          Need to use a different account? <Link to="/login" className="font-bold text-terracotta-dark hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
